import { useEffect, useRef } from 'react';
import flatpickr from 'flatpickr';
import type { Instance } from 'flatpickr/dist/types/instance';
import 'flatpickr/dist/flatpickr.min.css';

interface TimePickerProps {
  // "HH:mm:ss" (24h). Empty string = no value.
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  enableSeconds?: boolean;
}

// Flatpickr time-only picker — mirrors the legacy mar-instruments flatpickr config
// (enableTime + noCalendar + 24h). Value is a plain "HH:mm:ss" string.
export function TimePicker({ value, onChange, className, placeholder, enableSeconds = true }: TimePickerProps) {
  const ref = useRef<HTMLInputElement>(null);
  const fpRef = useRef<Instance | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!ref.current) return;
    const fp = flatpickr(ref.current, {
      enableTime: true,
      noCalendar: true,
      dateFormat: enableSeconds ? 'H:i:S' : 'H:i',
      time_24hr: true,
      enableSeconds,
      defaultDate: value || undefined,
      onChange: (_dates, str) => onChangeRef.current(str),
    }) as Instance;
    fpRef.current = fp;
    return () => fp.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fp = fpRef.current;
    if (fp && value !== fp.input.value) fp.setDate(value || '', false);
  }, [value]);

  return <input ref={ref} className={className ?? 'form-control form-control-sm'} placeholder={placeholder} defaultValue={value} />;
}
